const BASE = process.env.REACT_APP_API_BASE ?? "/api";

function headers(withJson = true) {
  const token = localStorage.getItem("fw_token");
  const h = {};
  if (withJson) h["Content-Type"] = "application/json";
  if (token) h["X-User-Token"] = token;
  return h;
}

export async function magicLink(username, password) {
  const r = await fetch(`${BASE}/auth/magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!r.ok) throw new Error("Auth failed");
  return r.json();
}

export async function validate() {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 6000);
  try {
    const r = await fetch(`${BASE}/auth/validate`, { headers: headers(false), signal: ctrl.signal });
    if (!r.ok) throw new Error("Invalid token");
    return r.json();
  } finally {
    clearTimeout(timer);
  }
}

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function getBrowserCoords(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    let done = false;
    const finish = (val) => { if (!done) { done = true; resolve(val); } };
    navigator.geolocation.getCurrentPosition(
      (pos) => finish({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => finish(null),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 600000 }
    );
  });
}

// Builds the optional geo body sent with /activity/start. Always includes the
// timezone (coarse fallback); includes precise coords only if the user opted in.
export async function buildGeoPayload() {
  const tz = getTimezone();
  const optedIn = localStorage.getItem("fw_geo_optin") === "1";
  if (optedIn) {
    const coords = await getBrowserCoords();
    if (coords) return { ...coords, tz };
  }
  return { tz };
}

export async function startActivity() {
  const body = await buildGeoPayload();
  const r = await fetch(`${BASE}/activity/start`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error("Cannot start");
  return r.json();
}

export async function getMapPoints(days = 7) {
  const r = await fetch(`${BASE}/activity/map?days=${days}`, {
    headers: headers(false),
    cache: "no-store",
  });
  if (!r.ok) throw new Error("Cannot load map");
  return r.json();
}

// "Where to eat" picker — all eateries within `radius` metres of lat/lon.
// `types` is an optional array of OSM categories; omit for all food types.
export async function searchFood({ lat, lon, radius = 1000, types } = {}) {
  const params = new URLSearchParams({ lat, lon, radius });
  if (types && types.length) params.set("types", types.join(","));
  const r = await fetch(`${BASE}/food/search?${params.toString()}`, {
    headers: headers(false),
    cache: "no-store",
  });
  if (!r.ok) throw new Error("Cannot load restaurants");
  return r.json();
}

// ── Group "match mode" ──────────────────────────────────
// The single global session. All read-only w.r.t. the DB. Real-time comes over
// SSE (see lib/ws.js openMatchStream); these are the control-plane calls.

async function matchPost(path) {
  const r = await fetch(`${BASE}/match/${path}`, { method: "POST", headers: headers(false) });
  if (!r.ok) throw new Error(String(r.status));
  return r.json();
}

export async function matchCurrent() {
  const r = await fetch(`${BASE}/match/current`, { headers: headers(false), cache: "no-store" });
  if (!r.ok) throw new Error(String(r.status));
  return r.json();
}
export const matchCreate = () => matchPost("create");
export const matchJoin = () => matchPost("join");
export const matchLeave = () => matchPost("leave");
export const matchClose = () => matchPost("close");

export async function matchBegin({ lat, lon, radius, types, deck }) {
  const r = await fetch(`${BASE}/match/begin`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({
      lat, lon, radius,
      types: types && types.length ? types.join(",") : null,
      deck: Array.isArray(deck) ? deck : null,
    }),
  });
  if (!r.ok) throw new Error(String(r.status));
  return r.json();
}

export async function matchVote(placeId, value) {
  const r = await fetch(`${BASE}/match/vote`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ placeId, value }),
  });
  if (!r.ok) throw new Error(String(r.status));
  return r.json();
}

export async function finishActivity(id, squares, endedAt = new Date().toISOString()) {
  const r = await fetch(`${BASE}/activity/${id}/finish`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ paperSquaresUsed: squares, endedAt })
  });
  if (!r.ok) throw new Error(`Cannot finish: ${r.status}`);
  return true;
}

export async function recent(limit = 5) {
  const r = await fetch(`${BASE}/activity/recent?limit=${limit}`, {
    headers: headers(false),
    cache: "no-store"
  });
  if (!r.ok) throw new Error("Cannot load recent");
  return r.json();
}

export async function getMe() {
  const r = await fetch(`${BASE}/user/me`, { headers: headers(false), cache: "no-store" });
  if (!r.ok) throw new Error("Cannot load me");
  return r.json();
}

export async function getStats() {
  const r = await fetch(`${BASE}/user/stats`, { headers: headers(false), cache: "no-store" });
  if (!r.ok) throw new Error("Cannot load stats");
  return r.json();
}

export async function uploadPfp(file) {
  const token = localStorage.getItem("fw_token");
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${BASE}/user/pfp`, {
    method: "POST",
    headers: token ? { "X-User-Token": token } : {},
    body: fd
  });
  if (!r.ok) throw new Error("Cannot upload pfp");
  return r.json();
}

export async function myOngoing() {
  const r = await fetch(`${BASE}/activity/me/ongoing`, {
    headers: headers(false),
    cache: "no-store"
  });
  if (!r.ok) throw new Error("Cannot load my ongoing");
  return r.json();
}

export async function getMyPokemon() {
  const r = await fetch(`${BASE}/activity/me/pokemon`, { headers: headers(false), cache: "no-store" });
  if (!r.ok) throw new Error("Cannot load pokemon");
  return r.json();
}

// ── Casino ──────────────────────────────────────────────

export async function getCasinoWallet() {
  const r = await fetch(`${BASE}/casino/wallet`, { headers: headers(false), cache: "no-store" });
  if (!r.ok) throw new Error("Cannot load wallet");
  return r.json();
}

export async function claimDaily() {
  const r = await fetch(`${BASE}/casino/claim-daily`, { method: "POST", headers: headers(false) });
  if (!r.ok) throw new Error("Cannot claim daily");
  return r.json();
}

export async function getCasinoLeaderboard() {
  const r = await fetch(`${BASE}/casino/leaderboard`, { headers: headers(false), cache: "no-store" });
  if (!r.ok) throw new Error("Cannot load leaderboard");
  return r.json();
}

export async function spinSlots(bet) {
  const r = await fetch(`${BASE}/casino/slots/spin`, {
    method: "POST", headers: headers(), body: JSON.stringify({ bet })
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(text || "Cannot spin");
  }
  return r.json();
}

export async function blackjackStart(bet) {
  const r = await fetch(`${BASE}/casino/blackjack/start`, {
    method: "POST", headers: headers(), body: JSON.stringify({ bet })
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(text || "Cannot start blackjack");
  }
  return r.json();
}

export async function blackjackHit() {
  const r = await fetch(`${BASE}/casino/blackjack/hit`, { method: "POST", headers: headers(false) });
  if (!r.ok) throw new Error("Cannot hit");
  return r.json();
}

export async function blackjackStand() {
  const r = await fetch(`${BASE}/casino/blackjack/stand`, { method: "POST", headers: headers(false) });
  if (!r.ok) throw new Error("Cannot stand");
  return r.json();
}

export async function blackjackDouble() {
  const r = await fetch(`${BASE}/casino/blackjack/double`, { method: "POST", headers: headers(false) });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(text || "Cannot double");
  }
  return r.json();
}
